import { FilterType, NativeFilterTypes, CustomFilterType } from 'design-system-web';
import { StatusListItem } from '../types/EventStatus';
import { TFunction } from 'i18next';
import { VisitorPassQueryResponse } from 'lane-shared/domains/visitorManagement/types';
import { isNoMatchesSeverity } from '../helpers/watchlistSeverityHelpers';

interface Props {
  dateFilter: NativeFilterTypes.DateRange | NativeFilterTypes.Date;
  statusListItems: StatusListItem[];
  hostName: string[];
  registeredFrom: string[];
  minDate: Date;
  maxDate: Date;
  timeZone?: string;
  t: TFunction<'translation', undefined>;
}

function VisitorLogTableFilters({
  dateFilter,
  statusListItems,
  hostName,
  registeredFrom,
  minDate,
  maxDate,
  timeZone,
  t,
}: Props): (FilterType | CustomFilterType<VisitorPassQueryResponse>)[] {
  return [
    {
      key: 'startDatetime',
      type: dateFilter,
      label:
        dateFilter === NativeFilterTypes.DateRange
          ? t('Date Range')
          : t('Date'),
      isPromoted: true,
      isFixed: true,
      maxDate,
      minDate,
      excludeFromClear: true,
      timeZone,
    },
    {
      key: 'status',
      type: NativeFilterTypes.Multiselect,
      label: t('web.admin.channel.visitor.log.columns.status'),
      options: statusListItems.map(status => ({
        label: status.label,
        value: status.value,
      })),
      isPromoted: true,
      isFixed: true,
    },
    {
      key: 'watchlistSeverityLevel',
      type: NativeFilterTypes.Multiselect,
      label: t('web.admin.channel.visitor.watchlist.watchlistLevel'),
      options: [
        {
          label: t('web.admin.channel.visitor.watchlist.noMatches'),
          value: 'WATCHLIST_SEVERITY_LEVEL_INVALID',
        },
        {
          label: t('web.admin.channel.visitor.watchlist.highRisk'),
          value: 'WATCHLIST_SEVERITY_LEVEL_HIGH',
        },
        {
          label: t('web.admin.channel.visitor.watchlist.mediumRisk'),
          value: 'WATCHLIST_SEVERITY_LEVEL_MEDIUM',
        },
        {
          label: t('web.admin.channel.visitor.watchlist.lowRisk'),
          value: 'WATCHLIST_SEVERITY_LEVEL_LOW',
        },
      ],
      isPromoted: true,
      isFixed: true,
      customFilterFn: (row, columnId, filterValue) => {
        const visitorPass = row.original;
        const selectedValues = filterValue as string[];

        if (!selectedValues || selectedValues.length === 0) {
          return true;
        }

        if (selectedValues.includes('WATCHLIST_SEVERITY_LEVEL_INVALID')) {
          const hasNoMatches =
            !visitorPass.visitorWatchlistMatchInfo ||
            visitorPass.visitorWatchlistMatchInfo.length === 0 ||
            visitorPass.visitorWatchlistMatchInfo.every(match =>
              isNoMatchesSeverity(match.watchlistSeverityLevel)
            );

          if (selectedValues.length === 1) {
            return hasNoMatches;
          }

          if (hasNoMatches) {
            return true;
          }
        }

        const hasMatchingSeverity =
          visitorPass.visitorWatchlistMatchInfo?.some(match =>
            selectedValues.includes(match.watchlistSeverityLevel)
          ) || false;

        return hasMatchingSeverity;
      },
    },
    {
      key: 'tenantName',
      type: NativeFilterTypes.Select,
      label: t('web.admin.channel.visitor.log.columns.hostCompany'),
      options: hostName.map(item => ({
        label: item,
        value: item,
      })),
    },
    {
      key: 'eventName',
      type: NativeFilterTypes.Select,
      label: t('web.admin.channel.visitor.log.columns.registeredFrom'),
      options: registeredFrom.map(item => ({
        label: item,
        value: item,
      })),
    },
  ];
}

export default VisitorLogTableFilters;
